import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import Alpine from "alpinejs";
import { registerSW } from "virtual:pwa-register";

// Post-processing imports (disabled for performance)
// import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
// import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
// import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
// import { FXAAShader } from "three/examples/jsm/shaders/FXAAShader.js";

/**
 * Quality Settings System
 * Detects hardware capabilities and provides performance presets
 */
const QualityPresets = {
  low: {
    pixelRatio: 1,
    particleMultiplier: 0.25,
    postProcessing: false,
  },
  medium: {
    pixelRatio: 1.5,
    particleMultiplier: 0.5,
    postProcessing: true,
  },
  high: {
    pixelRatio: 2,
    particleMultiplier: 1,
    postProcessing: true,
  },
};

function detectQualityLevel() {
  // Check hardware concurrency (CPU cores)
  const cores = navigator.hardwareConcurrency || 2;
  
  // Check device memory if available
  const memory = navigator.deviceMemory || 4;
  
  // Check if mobile device
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  // Check screen size (smaller screens likely weaker devices)
  const isSmallScreen = window.innerWidth < 1024;
  
  // Score-based detection
  let score = 0;
  score += cores >= 8 ? 2 : cores >= 4 ? 1 : 0;
  score += memory >= 8 ? 2 : memory >= 4 ? 1 : 0;
  score += isMobile ? -1 : 0;
  score += isSmallScreen ? -1 : 0;
  
  if (score >= 3) return 'high';
  if (score >= 1) return 'medium';
  return 'low';
}

// Initialize quality settings
const detectedQuality = detectQualityLevel();
window.qualitySettings = {
  current: detectedQuality,
  presets: QualityPresets,
  
  get() {
    return QualityPresets[this.current];
  },
  
  set(level) {
    if (!QualityPresets[level]) {
      console.warn(`Unknown quality level: ${level}. Using 'medium'.`);
      level = 'medium';
    }
    this.current = level;
    window.dispatchEvent(new CustomEvent('quality:change', { detail: this.get() }));
    console.log(`Quality set to: ${level}`, this.get());
  }
};

console.log(`Auto-detected quality level: ${detectedQuality}`);

// HeatDistortionEffect class disabled for performance
// Can be re-enabled if post-processing is needed in the future

const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    if (confirm("Ein Update ist verfügbar. Aktualisieren?")) {
      updateSW(true);
    }
  },
  onOfflineReady() {
    console.log("Offline ready");
  },
});

// Make updateSW available globally for dev mode
window.updateSW = updateSW;

import KidsRoom from "./rooms/kids_v1.js"; // kids_v0.js
import DustEffect from "./effects/dust_effect.js";
import PoisonEffect from "./effects/poison_effect.js";

window.clampAttributeValue = (attribute, values) => {
  if (!attribute) return;

  const value = values[attribute.name];
  const minValue = attribute.minValue || 0;
  const maxValue = attribute.maxValue || 100;

  if (typeof minValue === 'number' && value < minValue) {
    return minValue;
  } else if (typeof maxValue === 'number' && value > maxValue) {
    return maxValue;
  }
  return value;
}

/**
 * Base
 */
window.Alpine = Alpine;
Alpine.start();

const canvas = document.querySelector("canvas.webgl");
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

window.scene = new THREE.Scene();
scene.background = new THREE.Color(0xb5c2af); // Sage green background

// Update background color based on lighting
window.addEventListener("light:change", (event) => {
  const { lightBitmap } = event.detail;
  
  if (lightBitmap === "00") {
    // Very dark green when no lights
    scene.background = new THREE.Color(0x3d4a3a);
  } else if (lightBitmap === "01" || lightBitmap === "10") {
    // Medium green when one window is letting light through
    scene.background = new THREE.Color(0x7a8874);
  } else {
    // Light sage green when both lights are on (11)
    scene.background = new THREE.Color(0xb5c2af);
  }
});

/**
 * Init room
 */
window.gu = { time: { value: 0 } };
window.room = new KidsRoom();
scene.add(room.group);

window.addEventListener("attributes:change", (values) => {
  let particulates = values.detail.particulates || 0;
  let effluvium = values.detail.effluvium || 0;

  if (window.dustEffect) {
    window.dustEffect.setup(particulates);
  } else {
    window.dustEffect = new DustEffect(particulates);
    scene.add(dustEffect.group);
  }

  if (window.poisonEffect) {
    window.poisonEffect.setup(effluvium);
  } else {
    window.poisonEffect = new PoisonEffect(effluvium);
    scene.add(poisonEffect.group);
  }
});

/**
 * Camera
 */
window.camera = new THREE.PerspectiveCamera(
  25,
  sizes.width / sizes.height,
  0.1,
  100,
);
camera.position.set(...room.config.camera.position);
camera.quaternion.set(...room.config.camera.quaternion);
scene.add(camera);
camera.lookAt(new THREE.Vector3(...room.config.camera.lookAt));

// Controls
window.controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;
controls.enabled = false;

controls.maxAzimuthAngle = room.config.controls.maxAzimuthAngle;
controls.minAzimuthAngle = room.config.controls.minAzimuthAngle;
controls.maxPolarAngle = room.config.controls.maxPolarAngle;
controls.maxDistance = room.config.controls.maxDistance;

controls.maxTargetRadius = 1.5;

controls.autoRotateSpeed = 1.5;

controls.cursor.set(...room.config.camera.lookAt);
controls.target.set(...room.config.camera.lookAt);
controls.update();

/**
 * Renderer
 */
window.renderer = new THREE.WebGLRenderer({
  canvas: canvas,
  antialias: false, // Using FXAA post-process instead (cheaper on integrated GPUs)
});
renderer.setSize(sizes.width, sizes.height);

// Apply quality-based pixel ratio
const qualityPixelRatio = Math.min(window.devicePixelRatio, window.qualitySettings.get().pixelRatio);
renderer.setPixelRatio(qualityPixelRatio);

function updateRendererSize() {
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;

  // Update camera
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  // Update renderer with quality-adjusted pixel ratio
  const currentQuality = window.qualitySettings.get();
  const pixelRatio = Math.min(window.devicePixelRatio, currentQuality.pixelRatio);
  
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(pixelRatio);
}

window.addEventListener("resize", () => {
  setTimeout(updateRendererSize, 200);
});

// Listen for quality changes to update renderer
window.addEventListener("quality:change", () => {
  updateRendererSize();
});

window.addEventListener("attributes:change", (values) => {
  Alpine.store("values", values.detail);
});

let clock = new THREE.Clock();
// Heat distortion effect disabled for performance
// const heatDistortionEffect = new HeatDistortionEffect(scene, camera, renderer);

window.tick = () => {
  window.requestAnimationFrame(tick);

  const deltaTime = clock.getDelta();
  
  // Update global time uniform for GPU-based particle animations
  window.gu.time.value += deltaTime;
  
  controls.update();
  
  // Direct rendering (no post-processing)
  renderer.render(scene, camera);
};

tick();
